import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Popover, Radio, Tooltip } from "antd";
import { CalendarOutlined, DownOutlined, LeftOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import DatePicker from "../../core/components/DatePicker";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import { useApp } from "../../core/context/AppContext";
import { LocalStorageService } from "../../core/service/local-storage-service";
import { getDateFromNow } from "../../core/utils/date-utils";
import { useMediaQuery } from "../../core/utils/useMediaQuery";
import "./quick-filter-bar.css";

/*
 * Grid üstü hızlı filtre çubuğunun ortak parçaları (çekim: StudyQuickFilterBar, rapor: ReportQuickFilterBar).
 *
 * Parçaların kendi durumu yok (arama kutusunun yazılmakta olan metni hariç): basılı görünen her şey UYGULANAN
 * filtreden (queryModel.filter) hesaplanır ve her tık filtreyi hemen uygular. Sayfanın sol paneli uygulanan
 * filtreyi izleyip formunu eşitler, yani çubuk ile panel hiçbir zaman ayrışmaz.
 */

const { RangePicker } = DatePicker;

// Modaliteler id ile değil İSİMLE bağlanır: id kurulumdan kuruluma değişebilir.
// Varsayılanlar çekim grid'inin GetDataGrid loglarından (Eylül 2026, 7 gün): MR ve CT çubukta açık,
// kalanlar "Diğer" menüsünde bu kullanım sırasıyla; listede olmayan bir modalite sona, alfabetik eklenir.
export const VISIBLE_MODALITIES = ['MR', 'CT'];
export const MODALITY_ORDER = ['MR', 'CT', 'MG', 'OT', 'CR', 'US', 'DX', 'XA', 'PX', 'SR', 'AI'];

export const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

const sameDay = (a, b) => !!a && !!b && moment(a).isSame(moment(b), 'day');

const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));

// Beklenen değer uygulanan değerle eşleşiyor mu: diziler küme olarak, boş değerler birbirine eşit.
export const fieldMatches = (expected, actual) => {
    if (Array.isArray(expected))
        return sameSet(expected, isEmpty(actual) ? [] : actual);
    if (isEmpty(expected))
        return isEmpty(actual);
    return expected === actual;
};

const modalityName = (m) => `${m?.name ?? ''}`.trim().toUpperCase();

/**
 * Uygulanan filtre + değiştiriciler.
 * apply: değişiklikleri (bekleyenlerle birlikte) uygulanan filtrenin üstüne yazar; replace: filtrenin tamamını
 * değiştirir (Sıfırla).
 * stage: değişikliği sorgu atmadan bekletir (ör. tarih) — bir sonraki apply'da (Ara) birlikte uygulanır.
 * filter: uygulanan + bekleyen; çubuk bekleyen değeri gösterir. Uygulanan filtre değişince (Ara, başka bir düğme,
 * sol panel, Sıfırla) bekleyenler düşer.
 * windowFilterKey: sayfadan çıkıp dönünce sol panelin filtreyi geri yüklediği global ('studyFilter' / 'reportFilter').
 * ready: sol panel mount olup ilk filtreyi uygulayana kadar false — çubuk yarım durum göstermesin.
 */
export const useAppliedFilter = (windowFilterKey) => {
    const { queryModel, onFilterPageSort } = useDataGrid();
    const applied = queryModel?.filter || {};
    const [pending, setPending] = useState({});

    useEffect(() => {
        setPending({});
    }, [queryModel?.filter]);

    const replace = (next) => {
        setPending({});
        window[windowFilterKey] = next;
        onFilterPageSort({ filter: next });
    };

    return {
        filter: { ...applied, ...pending },
        apply: (changes) => replace({ ...applied, ...pending, ...changes }),
        stage: (changes) => setPending(prev => ({ ...prev, ...changes })),
        hasPending: Object.keys(pending).length > 0,
        replace,
        ready: Object.keys(applied).length > 0,
    };
};

/**
 * Serbest arama durumu. Arama yalnız Enter/ikonla uygulanır; yazılan metin o ana kadar yerel durumda bekler.
 * deferred: { stage, hasPending } verilirse kapsam değişikliği de sorgu atmaz, bekler; hasPending: bekletilen
 * değişiklik (tarih/kapsam) var mı — varsa metin aynı kalsa da Ara onları uygular.
 * active: kutuda değer olduğu an (henüz uygulanmasa da) true.
 * inDateRange: arama kapsamı "seçili tarih aralığı" (filter.searchInDateRange) — sunucu aramayı tarihlerle de süzer.
 * dateLocked: tarih bloğu kilitli mi — arama doluyken ve kapsam "tüm zamanlar" iken, çünkü arama o zaman tüm
 * tarihlerde çalışır (sol panel de aynı davranır).
 */
export const useQuickSearch = (filter, apply, deferred) => {
    const hasPending = !!deferred?.hasPending;
    const applied = filter.searchText || '';
    const [text, setText] = useState(applied);

    // Arama sol panelden yapıldıysa (ya da "Sıfırla" ile silindiyse) kutu da uygulanan değeri göstersin.
    useEffect(() => {
        setText(applied);
    }, [applied]);

    // Boşaltıldığında alan tamamen kalksın; boş string sunucuya gitmesin.
    // antd temizleme ikonunda da onSearch('') çağırır → arama kaldırılıp tarih filtresi geri gelir.
    const submit = (value) => {
        const next = `${value ?? ''}`.trim() || undefined;
        if (next === (applied || undefined) && !hasPending)
            return;
        apply({ searchText: next });
    };

    // Çubuktaki Ara düğmesi: bekleyen bir şey olmasa da sorguyu yeniden atar (kutudaki metin + bekleyenlerle).
    const run = () => apply({ searchText: `${text ?? ''}`.trim() || undefined });

    const active = !!text?.trim();
    const inDateRange = filter.searchInDateRange === true;

    // Bekletmeli çubukta kapsam yalnız işaretlenir, Ara'yla uygulanır. Değilse kapsam değişince kutuda yazılı
    // (henüz uygulanmamış olsa da) metin de birlikte uygulanır.
    const setScope = (next) => deferred?.stage
        ? deferred.stage({ searchInDateRange: next })
        : apply({
            searchInDateRange: next,
            ...(active ? { searchText: text.trim() } : {}),
        });

    return { text, setText, submit, run, active, inDateRange, dateLocked: active && !inDateRange, setScope, pending: hasPending };
};

/**
 * Arama kartı. scopeDates verilirse kutuya odaklanınca altında küçük bir panel açılır: açıklama (hint) +
 * aramanın kapsamı (tüm zamanlar / seçili tarih aralığı); "seçili tarih aralığı"nda altında scopeDates
 * (ör. aynı alanları yöneten QuickDateRange) gösterilir; panelin altında aramayı uygulayan küçük bir Ara düğmesi
 * var. Verilmezse açıklama tooltip'te durur.
 */
export function QuickSearch({ search, hint, scopeDates }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const cardRef = useRef(null);
    const panelRef = useRef(null);

    // Panel yalnız dışarı tıklanınca kapanır (odak kaybında değil): tarih seçicinin takvimi ayrı bir katmanda
    // açıldığından ona tıklamak da "dışarısı" sayılmaz.
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (e) => {
            const el = e.target;
            if (cardRef.current?.contains(el) || panelRef.current?.contains(el) || el.closest?.('.ant-picker-dropdown'))
                return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    const scoped = !!scopeDates;

    const card = (
        <div
            ref={cardRef}
            className={`qf-search${search.active ? ' is-active' : ''}${search.pending ? ' is-pending' : ''}`}
            onFocus={scoped ? () => setOpen(true) : undefined}
            onKeyDown={scoped ? (e) => { if (e.key === 'Escape') setOpen(false); } : undefined}
            // Tab ile başka bir alana geçilince kapanır; panelin içine geçerken açık kalır.
            onBlur={scoped ? (e) => {
                const next = e.relatedTarget;
                if (next && !panelRef.current?.contains(next) && !cardRef.current?.contains(next))
                    setOpen(false);
            } : undefined}
        >
            <Input.Search
                size="small"
                allowClear
                value={search.text}
                prefix={scoped && search.inDateRange
                    ? <CalendarOutlined className="qf-search-scope" aria-label={t('QF_SEARCH_SCOPE_RANGE')} />
                    : undefined}
                placeholder={t('Type anything: name, ID, number...')}
                onChange={(e) => search.setText(e.target.value)}
                onSearch={search.submit}
            />
        </div>
    );

    if (!scoped) {
        return (
            <Tooltip title={hint} mouseEnterDelay={0.6} placement="bottomLeft">
                {card}
            </Tooltip>
        );
    }

    return (
        <Popover
            open={open}
            placement="bottomLeft"
            arrow={false}
            content={(
                <div ref={panelRef} className="qf-scope">
                    {hint && <div className="qf-scope-hint">{hint}</div>}
                    <span className="qf-panel-label">{t('QF_SEARCH_SCOPE')}</span>
                    <Radio.Group value={search.inDateRange} onChange={(e) => search.setScope(e.target.value)}>
                        <Radio value={false}>{t('QF_SEARCH_SCOPE_ALL')}</Radio>
                        <Radio value={true}>{t('QF_SEARCH_SCOPE_RANGE')}</Radio>
                    </Radio.Group>
                    {search.inDateRange && <div className="qf-scope-dates">{scopeDates}</div>}
                    <div className="qf-scope-actions">
                        <Button
                            size="small"
                            type={search.pending ? 'primary' : 'default'}
                            icon={<SearchOutlined />}
                            onClick={() => {
                                search.submit(search.text);
                                setOpen(false);
                            }}
                        >
                            {t('Search')}
                        </Button>
                    </div>
                </div>
            )}
        >
            {card}
        </Popover>
    );
}

/**
 * Çubuğun en altındaki Ara satırı (solda): tarih gibi bekletilen değişiklikleri ve kutudaki metni uygular.
 * Bekleyen değişiklik varken dolu renkte durur ki basılması gerektiği görünsün.
 */
export function QuickSearchRow({ search, children }) {
    const { t } = useTranslation();

    return (
        <div className="qf-row qf-row-actions">
            <Button
                size="small"
                type={search.pending ? 'primary' : 'default'}
                icon={<SearchOutlined />}
                onClick={search.run}
            >
                {t('Search')}
            </Button>
            {children}
        </div>
    );
}

/**
 * Tarih bloğu: hazır aralıklar + ‹ aralık › + aralık seçici. beginKey/endKey yönetilen filtre alanlarıdır
 * (sayfa, panelde hangi tarih türü seçiliyse onu verir). presets: [{ key, label, option: DATE_OPTIONS.* }].
 * allowClear: seçicinin çarpısıyla tarih filtresi tamamen kaldırılabilir (tarihsiz sorgu sayfanın kaldırabildiği yerde).
 * separate: tek aralık seçici yerine ayrı başlangıç ve bitiş seçicileri; allowClear ile her uç tek başına
 * kaldırılabilir (yalnız başlangıç = o günden sonrası, yalnız bitiş = o güne kadar).
 */
export function QuickDateRange({ filter, apply, beginKey, endKey, presets, disabled, label, allowClear = false, separate = false }) {
    const { t } = useTranslation();
    // Dokunmatik ekranda tarih kutusu yazılamaz: dokununca klavye açılıp takvimin üstünü kapatıyordu.
    const isTouch = useMediaQuery('(pointer: coarse)');

    const begin = filter[beginKey] ? moment(filter[beginKey]) : null;
    const end = filter[endKey] ? moment(filter[endKey]) : null;

    const applyDates = (start, finish) => apply({ [beginKey]: start, [endKey]: finish });

    const activePreset = presets.find(p => {
        const d = getDateFromNow(p.option);
        return sameDay(begin, d.start) && sameDay(end, d.end);
    })?.key;

    // Aralığı kendi uzunluğu kadar kaydırır: tek gün → ±1 gün, 6 günlük aralık → önceki/sonraki 6 gün.
    // İleri giderken bitiş bugünü geçmez.
    const shiftDates = (direction) => {
        const span = end.clone().startOf('day').diff(begin.clone().startOf('day'), 'days') + 1;
        const nextBegin = begin.clone().add(direction * span, 'days');
        const nextEnd = moment.min(end.clone().add(direction * span, 'days'), moment());
        applyDates(nextBegin, nextEnd);
    };
    const canGoNext = !!end && end.isBefore(moment(), 'day');

    const pickerProps = {
        size: 'small',
        allowClear,
        inputReadOnly: isTouch,
        format: 'DD.MM.YY',
        disabled,
        locale: LocalStorageService.getAntLocale(),
    };

    return (
        <div className={`qf-group${disabled ? ' is-disabled' : ''}`} role="group" aria-label={label}>
            <div className="qf-seg">
                {presets.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        disabled={disabled}
                        aria-pressed={activePreset === p.key}
                        onClick={() => {
                            const d = getDateFromNow(p.option);
                            applyDates(d.start.clone(), d.end.clone());
                        }}
                    >
                        {t(p.label)}
                    </button>
                ))}
            </div>
            <div className={`qf-stepper${separate ? ' qf-stepper-separate' : ''}`}>
                <Tooltip title={t('Previous')}>
                    <Button
                        size="small"
                        icon={<LeftOutlined />}
                        disabled={disabled || !begin || !end}
                        onClick={() => shiftDates(-1)}
                    />
                </Tooltip>
                {separate ? (
                    <>
                        {/* Başlangıç bitişi geçerse bitiş de ona çekilir (ve tersi) ki aralık hiç ters dönmesin. */}
                        <DatePicker
                            {...pickerProps}
                            placeholder={t('Start Date')}
                            value={begin}
                            onChange={(d) => {
                                if (d)
                                    applyDates(d, end && end.isBefore(d, 'day') ? d.clone() : end);
                                else if (allowClear)
                                    applyDates(null, end);
                            }}
                        />
                        <DatePicker
                            {...pickerProps}
                            placeholder={t('End Date')}
                            value={end}
                            onChange={(d) => {
                                if (d)
                                    applyDates(begin && begin.isAfter(d, 'day') ? d.clone() : begin, d);
                                else if (allowClear)
                                    applyDates(begin, null);
                            }}
                        />
                    </>
                ) : (
                    <RangePicker
                        {...pickerProps}
                        value={[begin, end]}
                        onChange={(range) => {
                            if (range?.[0] && range?.[1])
                                applyDates(range[0], range[1]);
                            // Çarpı: antd onChange(null) çağırır → iki uç birlikte kalkar.
                            else if (!range && allowClear)
                                applyDates(null, null);
                        }}
                    />
                )}
                <Tooltip title={t('Next')}>
                    <Button
                        size="small"
                        icon={<RightOutlined />}
                        disabled={disabled || !begin || !canGoNext}
                        onClick={() => shiftDates(1)}
                    />
                </Tooltip>
            </div>
        </div>
    );
}

/** Tekli seçim: options [{ value, label }], seçili olan value ile tam eşit olan. */
export function QuickSegment({ options, value, onChange, ariaLabel }) {
    return (
        <div className="qf-seg" role="group" aria-label={ariaLabel}>
            {options.map(option => (
                <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={option.value === value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

/** Aç/kapa çip. */
export function QuickChip({ pressed, onClick, icon, children, className }) {
    return (
        <button
            type="button"
            className={className ? `qf-chip ${className}` : 'qf-chip'}
            aria-pressed={!!pressed}
            onClick={onClick}
        >
            {icon}
            {children}
        </button>
    );
}

/** Modalite çipleri (çoklu): VISIBLE_MODALITIES açıkta, kalanlar "Diğer" menüsünde. */
export function QuickModalityFilter({ filter, apply }) {
    const { t } = useTranslation();
    const { modalities } = useApp();

    const sortedModalities = useMemo(() => {
        const rank = (m) => {
            const i = MODALITY_ORDER.indexOf(modalityName(m));
            return i === -1 ? MODALITY_ORDER.length : i;
        };
        return [...(modalities || [])]
            .sort((a, b) => rank(a) - rank(b) || modalityName(a).localeCompare(modalityName(b)));
    }, [modalities]);

    if (sortedModalities.length === 0)
        return null;

    const selectedIds = filter.modalities || [];
    const visibleModalities = sortedModalities.filter(m => VISIBLE_MODALITIES.includes(modalityName(m)));
    const otherModalities = sortedModalities.filter(m => !VISIBLE_MODALITIES.includes(modalityName(m)));
    const selectedOthers = otherModalities.filter(m => selectedIds.includes(m.id));

    const toggleModality = (id) => apply({
        modalities: selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id],
    });

    const setOtherModalities = (ids) => apply({
        modalities: [
            ...selectedIds.filter(id => !otherModalities.some(m => m.id === id)),
            ...ids,
        ],
    });

    return (
        <div className="qf-group" role="group" aria-label={t('Modality')}>
            {visibleModalities.map(m => (
                <button
                    key={m.id}
                    type="button"
                    className="qf-chip qf-chip-code"
                    aria-pressed={selectedIds.includes(m.id)}
                    onClick={() => toggleModality(m.id)}
                >
                    {m.name}
                </button>
            ))}
            {otherModalities.length > 0 && (
                <Popover
                    trigger="click"
                    placement="bottomLeft"
                    content={(
                        <Checkbox.Group
                            className="qf-more"
                            value={selectedOthers.map(m => m.id)}
                            onChange={setOtherModalities}
                        >
                            {otherModalities.map(m => (
                                <Checkbox key={m.id} value={m.id}>{m.name}</Checkbox>
                            ))}
                        </Checkbox.Group>
                    )}
                >
                    <button
                        type="button"
                        className="qf-chip qf-chip-code qf-chip-more"
                        aria-pressed={selectedOthers.length > 0}
                    >
                        {selectedOthers.length > 0 ? selectedOthers.map(m => m.name).join(', ') : t('Other')}
                        <DownOutlined />
                    </button>
                </Popover>
            )}
        </div>
    );
}

export function QuickResetLink({ onClick }) {
    const { t } = useTranslation();

    return (
        <button type="button" className="qf-link qf-reset" onClick={onClick}>
            {t('Reset')}
        </button>
    );
}
